import { Alert, Group, NumberInput, SimpleGrid, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import type { CashPair } from '../../components/CashFigures';
import { formatMoney } from '../../lib/format';
import type { PortalLocale } from '../../lib/locale';
import { registerPageCopy } from './registerPageCopy';

/**
 * THE ONE COUNT SHEET, used everywhere the owner counts money.
 *
 * There are four places on this site where he types what he actually found — a cash drawer pulled
 * mid-shift, the safe, the change box, and a night's pouch opened the next morning. They are the
 * same gesture with a different subject, and the arithmetic behind them is the one number in this
 * system a person can check by hand against the notes in their fingers. Four copies of that would
 * drift, and the first sign of drift would be two screens disagreeing about a shortage.
 *
 * ## What it deliberately does NOT do
 *
 * **It never accepts a total.** Quantities of each note go in; the server multiplies and stores its
 * own answer. Over and short has never been a figure anybody types here, and the moment a box on a
 * screen takes one it is a figure somebody can make agree.
 *
 * **It never shows the expectation.** Every caller submits first and is answered afterwards. The
 * owner is genuinely allowed to know what should be there — the blind-count rule protects the person
 * the variance would be attributed to, and that is not him — but a figure on screen while somebody
 * counts is a figure the counting drifts towards.
 *
 * **A blank is not a nought.** "I looked in the twenties box and there were none" is a fact a
 * variance is read against; "I did not look" is not. Only the boxes with something typed in them
 * become lines, and a zero typed on purpose travels as a zero.
 */

export interface Denomination {
  id: string;
  currency: 'USD' | 'HNL';
  valueMinor: number;
  active: boolean;
}

/** Quantities keyed by the note they are quantities of: `USD:2000` is the twenties box. */
export type Sheet = Record<string, number | ''>;

export const keyOf = (d: Pick<Denomination, 'currency' | 'valueMinor'>) => `${d.currency}:${d.valueMinor}`;

/** The sheet as the API wants it: one line per note somebody actually looked in. */
export function linesOf(sheet: Sheet, notes: Denomination[]) {
  return notes
    .filter((note) => sheet[keyOf(note)] !== '' && sheet[keyOf(note)] !== undefined)
    .map((note) => ({
      currency: note.currency,
      denominationMinor: note.valueMinor,
      qty: Number(sheet[keyOf(note)]),
    }));
}

/**
 * What the sheet says it adds up to, shown live beside the boxes.
 *
 * This is NOT the figure that gets recorded — the server recomputes it from the lines and stores its
 * own answer. It is here because somebody counting a drawer at nine in the evening wants the total
 * to move as they type, and finding out at the end that a quantity went in the wrong box is much
 * cheaper than finding out in a variance report tomorrow.
 *
 * Integer minor units throughout, exactly as the backend does it, and the two currencies never
 * touch: a box holding two hundred dollars and four thousand lempiras holds exactly that.
 */
export function totalOf(sheet: Sheet, notes: Denomination[]): CashPair {
  let usdMinor = 0;
  let hnlMinor = 0;
  for (const note of notes) {
    const qty = Number(sheet[keyOf(note)] || 0);
    if (!Number.isFinite(qty)) continue;
    if (note.currency === 'USD') usdMinor += note.valueMinor * qty;
    else hnlMinor += note.valueMinor * qty;
  }
  return { usdMinor, hnlMinor };
}

/**
 * The notes this bar takes, the sheet being typed into it, and what it comes to.
 *
 * The list is read from the site's own denominations rather than from a constant — `money.md` is
 * explicit that which notes exist is configuration, and a bar that starts taking a L 1,000 note adds
 * a row rather than waiting for a deploy.
 *
 * `initial` exists for the one caller that EDITS a stored composition — the change box's standing
 * float — where the sheet has to open reading what is on file, so that blanking a note is a
 * deliberate act rather than the state every box starts in. A COUNT must never pass it: seeding a
 * count sheet is putting the answer on the screen while somebody counts, which is exactly what the
 * blind-count rule exists to prevent.
 */
export function useCountSheet(initial: Sheet = {}) {
  const [sheet, setSheet] = useState<Sheet>(initial);
  const denominations = useQuery({
    queryKey: listKey('/admin/v1/pricing/denominations'),
    queryFn: () => rawApi<Denomination[]>('GET', '/admin/v1/pricing/denominations'),
  });
  const notes = (denominations.data ?? []).filter((d) => d.active !== false);
  return {
    sheet,
    setSheet,
    notes,
    loading: denominations.isLoading,
    /*
     * The read failing is a different fact from the bar having no notes, and every caller needs the
     * difference: with the query in flight or failed, `notes` is [] whatever the truth is, so
     * `lines()` is [] and `anythingTyped` is false REGARDLESS of what is stored or seeded. A caller
     * that reads the empty sheet as "nothing here" while this is set is reporting a network blink
     * as a fact about the money.
     */
    error: denominations.error,
    lines: () => linesOf(sheet, notes),
    running: totalOf(sheet, notes),
    anythingTyped: notes.some((n) => sheet[keyOf(n)] !== '' && sheet[keyOf(n)] !== undefined),
    clear: () => setSheet({}),
  };
}

export function CountSheet({
  locale = 'en',
  notes,
  loading,
  error,
  sheet,
  onChange,
  blankReads,
}: {
  /** Locale of the workflow that opened this shared sheet. Existing English callers may omit it. */
  locale?: PortalLocale;
  notes: Denomination[];
  loading: boolean;
  /**
   * The denominations query's failure, when it failed — `useCountSheet`'s own `error`. An empty
   * sheet after a FAILED read must never wear the "no notes listed" sentence below: that sentence
   * sends the owner to the pricing screen to fix configuration that is not broken, when the truth
   * is that this page could not reach the office.
   */
  error?: unknown;
  sheet: Sheet;
  onChange: (key: string, value: number | '') => void;
  /** What an empty box means here. "Not counted" on a count; the float editor says its own word. */
  blankReads?: string;
}) {
  const text = registerPageCopy(locale).waiting.audit.sheet;
  const emptyValue = blankReads ?? text.blankReads;
  return (
    <>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        <NoteColumn
          title={text.dollars}
          notes={notes.filter((d) => d.currency === 'USD')}
          sheet={sheet}
          onChange={onChange}
          blankReads={emptyValue}
          noteQuantity={text.noteQuantity}
        />
        <NoteColumn
          title={text.lempiras}
          notes={notes.filter((d) => d.currency === 'HNL')}
          sheet={sheet}
          onChange={onChange}
          blankReads={emptyValue}
          noteQuantity={text.noteQuantity}
        />
      </SimpleGrid>
      {/* Only a read that SUCCEEDED and came back empty means the bar has no notes. A failed read
          shows what actually happened; retrying is reopening this dialog once the connection is back. */}
      {notes.length === 0 && !loading && error != null && (
        <Alert color="red" mt="sm">
          {text.loadFailed}
        </Alert>
      )}
      {notes.length === 0 && !loading && error == null && (
        <Alert color="gray" mt="sm">
          {text.noneConfigured}
        </Alert>
      )}
    </>
  );
}

function NoteColumn({
  title,
  notes,
  sheet,
  onChange,
  blankReads,
  noteQuantity,
}: {
  title: string;
  notes: Denomination[];
  sheet: Sheet;
  onChange: (key: string, value: number | '') => void;
  blankReads: string;
  noteQuantity: (amount: string) => string;
}) {
  if (notes.length === 0) return null;
  return (
    <div>
      <Text size="sm" fw={600} mb={6}>
        {title}
      </Text>
      <Stack gap={6}>
        {notes.map((note) => {
          const key = keyOf(note);
          const qty = sheet[key];
          const subtotal = Number(qty || 0) * note.valueMinor;
          return (
            <Group key={note.id} gap="sm" wrap="nowrap">
              <Text size="sm" w={90} ta="right">
                {formatMoney(note.valueMinor, note.currency)}
              </Text>
              <NumberInput
                size="xs"
                w={90}
                min={0}
                allowDecimal={false}
                aria-label={noteQuantity(formatMoney(note.valueMinor, note.currency))}
                value={qty ?? ''}
                onChange={(v) => onChange(key, v === '' ? '' : Number(v))}
              />
              <Text size="xs" c="dimmed">
                {qty === '' || qty === undefined ? blankReads : formatMoney(subtotal, note.currency)}
              </Text>
            </Group>
          );
        })}
      </Stack>
    </div>
  );
}
